<template>
  <div id="app">
    <loader ref="loader"/>
    <toaster ref="toaster"/>
    <modalConfirmation ref="confirmation"/>
    <router-view/>
  </div>
</template>

<script>
import auth from '@/services/auth'
import local from '@/local'
import logger from '@/services/logger'
import taber from '@/services/taber'
import translation from '@/services/translation'
import version from '@/services/version'

// Close websockets
window.addEventListener('beforeunload', auth.closeWebSockets.bind(auth))

// Handle click
window.addEventListener('click', (e) => {
  document.querySelectorAll('.dropdown,.selector').forEach(item => {
    if (!item.contains(e.target) && e.target !== item) item.classList.remove('active')
    else {
      // Invalid item
      if (item !== e.target && !item.contains(e.target)) return

      // Focus
      item.classList.toggle('active')
    }
  })
})

// Handle auto upgrade
var lastVisible
var autoUpgradeEnabled = true
window.enableAutoUpgrade = () => { autoUpgradeEnabled = true }
window.disableAutoUpgrade = () => { autoUpgradeEnabled = false }
window.addEventListener('load', (e) => {
  lastVisible = new Date()
})
window.addEventListener('visibilitychange', async function (e) {
  if (!document.hidden && lastVisible && autoUpgradeEnabled) {
    var n = new Date()
    if (n.getTime() - lastVisible.getTime() > 30 * 1000) {
      try {
        const v = await version.get()
        if (v && v.mandatory.value !== local.version) document.location.reload(true)
      } catch (err) {
        logger.error(err)
      }
    }
    lastVisible = n
  }
})

// Handle logout on other tab
taber.on('logout', data => {
  // Logout
  auth._logout(false, false, translation.route({
    name: 'login',
    params: { logout_previous_user_email: data.previous_user_email }
  }))
})

export default {}
</script>

<style>

@font-face {
  font-family: 'AvenirNext';
  src: url('assets/font/AvenirNext-Regular.ttf');
}

@font-face {
  font-family: 'AvenirNext';
  font-weight: bold;
  src: url('assets/font/AvenirNext-Bold.ttf');
}

@font-face {
  font-family: 'AvenirNext';
  font-weight: 600;
  src: url('assets/font/AvenirNext-Demi.ttf');
}

@font-face {
  font-family: 'AvenirNext';
  font-style: italic;
  src: url('assets/font/AvenirNext-It.ttf');
}

/* Font size start */

/* 4k screens */
@media (min-width: 3840px) {

    html, body {
      font-size: 36px;
    }

}

/* 1920p computers */
@media (min-width: 1920px) and (max-width: 3839px) {

    html, body {
      font-size: 18px;
    }

}

/* 1680p computers */
@media (min-width: 1680px) and (max-width: 1919px) {

    html, body {
      font-size: 16px;
    }

}

/* 1440p computers */
@media (min-width: 1440px) and (max-width: 1679px) {

    html, body {
      font-size: 14px;
    }

}

/* 1280p computers */
@media (min-width: 1280px) and (max-width: 1439px) {

    html, body {
      font-size: 12px;
    }

}

/* Others
   Beware as it doesn't look good when downscaling on the Mac but it does look good
   when using the browser simulator */
@media (max-width: 1279px) {

    html, body {
      font-size: 14px;
    }

}

/* Font size end */

/* Large desktop */
@media (min-width: 1200px) {

    .panel {
        padding: 3em;
    }

}

/* Portrait tablet to landscape and desktop */
@media (min-width: 768px) and (max-width: 1199px) {

    .panel {
        padding: 3em;
    }

}

/* Landscape phone to portrait tablet */
@media (min-width: 481px) and (max-width: 767px) {

    .panel {
        padding: 3em;
    }

}

/* Landscape phones and down */
@media (max-width: 480px) {

    .panel {
        padding: 2em;
    }

}

* {
  box-sizing: border-box;
}

*:focus {
    outline: none;
}

html, body {
  background-color: #201c1d;
  color: #ffffff;
  font-family: 'AvenirNext';
  height: 100%;
  margin: 0;
  padding: 0;
  width: 100%;
}

#app {
  height: 100%;
  width: 100%;
}

.template {
  height: 100%;
  width: 100%;
}

.template > .content {
    text-align: center;
    width: 100%;
}

button {
    -webkit-tap-highlight-color: transparent;
    background-color: #ff6e20;
    border: none;
    border-radius: 2em;
    color: #ffffff;
    cursor: pointer;
    font-family: 'AvenirNext';
    font-size: 1em;
    font-weight: 400;
    padding: 0.9em 1.7em;
}

button:hover {
  background-color: #fa8740;
}

.panel {
    background-color: #ffffff;
    border-radius: 0.5em;
    color: rgba(0, 0, 0, 0.4);
    display: inline-block;
    margin: auto;
}

.panel a, .panel a:visited {
  color: #000000;
  text-decoration: none;
}

form {
  text-align: center; /* center the button */
}

.panel .title, form .title {
  color: #000000;
  font-size: 2em;
  font-weight: 400;
  margin: 0 0 1em 0;
  text-align: center;
}

form .description {
  color: #000000;
  margin-bottom: 2em;
}

form > div {
  margin: 0 0 1.5em 0;
  text-align: left;
}

form label {
  display: block;
  margin: 0 0 0.3em 0;
}

form input::placeholder {
  opacity: 0.4;
}

form input, form textarea {
  border: solid 1px #e0e0e0;
  border-radius: 0.3em;
  display: block;
  font-family: 'AvenirNext';
  font-size: 1em;
  font-weight: 400;
  padding: 0.8em 1em;
  margin: 0 0 0.5em 0;
  width: 100%;
}

form textarea {
  resize: none;
}

form input:focus, form textarea:focus {
  border-color: #959595;
}

form input.error, form textarea.error {
  border-color: #da3832;
}

form > div > div.error {
  color: #da3832;
  margin: 0 0 0.3em 0;
}

.dropdown .content {
  background-color: #000;
  border-radius: 0.3em;
  color: #fff;
  z-index: 10;
  display: none;
  position: absolute;
  right: 0;
}

.dropdown.active .content {
  display: block;
}

.dropdown .content > div {
  padding: 0.5em 1em;
}

.dropdown .content > div:hover {
  background-color: rgba(255, 255, 255, 0.2);
  border-radius: 0.3em;
  color: #ff6e20;
  cursor: pointer;
}

</style>
